import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { User, Save, Camera } from 'lucide-react';

export default function ProfilePage() {
  const { user, fetchUser } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    display_name: user?.display_name || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const picInputRef = useRef(null);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/auth/profile', form);
      await fetchUser();
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploadingPic(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/auth/profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Profile picture updated!');
      if (fetchUser) await fetchUser();
    } catch {
      toast.error('Failed to upload picture');
    } finally {
      setUploadingPic(false);
      if (picInputRef.current) picInputRef.current.value = '';
    }
  };

  return (
    <div className="container-app py-8 md:py-12" data-testid="profile-page">
      <h1 className="text-2xl md:text-3xl font-bold text-stone-900 mb-8">Your Profile</h1>

      <div className="max-w-lg">
        <Card className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              {/* Profile picture with hover-to-upload */}
              <div className="relative group">
                <div
                  className="w-14 h-14 rounded-full overflow-hidden bg-orange-100 flex items-center justify-center border-2 border-orange-200"
                  data-testid="profile-pic-preview"
                >
                  {user?.profile_picture_url ? (
                    <img
                      src={user.profile_picture_url}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <User className="w-7 h-7 text-orange-700" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => picInputRef.current?.click()}
                  disabled={uploadingPic}
                  className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  aria-label="Upload profile picture"
                  data-testid="profile-pic-upload-btn"
                >
                  <Camera className="w-4 h-4 text-white" />
                </button>
                <input
                  ref={picInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePicUpload}
                  className="hidden"
                  data-testid="profile-pic-file-input"
                />
              </div>
              <div>
                <CardTitle className="text-lg">{user?.display_name || user?.full_name}</CardTitle>
                <p className="text-sm text-stone-400">{user?.email}</p>
              </div>
            </div>
            <p className="text-xs text-stone-400 mt-2">
              Hover your photo and click <Camera className="w-3 h-3 inline -mt-0.5" /> to change it.
              {uploadingPic && <span className="text-orange-600 ml-1">Uploading…</span>}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4 mt-4">
              <div>
                <Label className="text-stone-700 text-sm font-medium">Full Name</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm(f => ({...f, full_name: e.target.value}))}
                  className="mt-1 rounded-xl border-stone-200 bg-stone-50 focus:bg-white h-12"
                  data-testid="profile-full-name-input"
                />
              </div>
              <div>
                <Label className="text-stone-700 text-sm font-medium">Display Name</Label>
                <Input
                  value={form.display_name}
                  onChange={(e) => setForm(f => ({...f, display_name: e.target.value}))}
                  className="mt-1 rounded-xl border-stone-200 bg-stone-50 focus:bg-white h-12"
                  data-testid="profile-display-name-input"
                />
              </div>
              <div className="pt-2">
                <p className="text-xs text-stone-400 mb-1">Email</p>
                <p className="text-sm text-stone-600">{user?.email}</p>
                <p className="text-xs text-stone-400 mt-2">Role: <span className="capitalize">{user?.role}</span></p>
              </div>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-full bg-orange-600 hover:bg-orange-700 text-white font-medium px-8 py-5 h-auto"
                data-testid="profile-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Danger zone: account deletion (Apple App Store guideline 5.1.1(v)) */}
        <Card className="bg-white rounded-2xl border border-stone-100 mt-6">
          <CardContent className="p-6">
            <p className="text-xs text-stone-400 uppercase tracking-wider font-medium mb-1">
              Danger zone
            </p>
            <p className="text-sm text-stone-600 mb-3">
              Permanently delete your account. Your historical pledges and activity will be
              anonymised so your supporters&rsquo; records stay intact.
            </p>
            <Link
              to="/account/delete"
              className="inline-flex items-center text-sm text-red-600 hover:text-red-700 font-medium"
              data-testid="profile-delete-account-link"
            >
              Delete my account &rarr;
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
